import { Button, Checkbox, DatePicker, message, Select, Space, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { serverFetch } from "@server";
import "../records/records.css";
import "../company/company.css";
import "../orders/order.css";
import RecordsHeader from "../records/records-header";
import RecordsLine from "../records/records-line";
import dayjs from "dayjs";
import Record from "../records/record";
import RecordsItem from "../records/records-item";
import { strDate2Server } from "@extensions/convert";
import { refreshButton } from "@extensions/design";
import { tasks } from "@extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";


const hourStart = 7;
const hourFinish = 23;

/** Планирование */
const TaskPlans = () => {
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftTaskId, setDraftTaskId] = useState(1);
  const [draftDate, setDraftDate] = useState(() => dayjs());
  const [draftShowDeleted, setDraftShowDeleted] = useState(false);
  const [draftSelectedUserId, setDraftSelectedUserId] = useState(null);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const recordsContainerRef = useRef(null);
  const [mobileGridWidth, setMobileGridWidth] = useState(0);
  const [date, setDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(); // Выбранное время для добавления новой записи
  const [selectedItem, setSelectedItem] = useState(); // Выбранная запись
  const [records, setRecords] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [userColWidth, setUserColWidth] = useState(() => {
    // Загружаем сохраненную ширину из localStorage
    const saved = localStorage.getItem('records-userColWidth');
    return saved ? parseInt(saved) : 200;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [taskId, setTaskId] = useState(1);

  const getUsers = () => {
    serverFetch("plan/dep")
      .then(data => setUsers([{ id: null, name: "Не назначен", active: true }, ...data.filter(i => i.active)]))
      .catch(e => message.error(e.userMessage));
  };

  const getRecords = () => {
    serverFetch(`plan/doc/planned?dateBeg=${strDate2Server(date)}&dateEnd=${strDate2Server(date)}&taskId=${taskId}`)
      .then(data => {
        const newRecords = data.map(i => ({ ...i,
          start: i.startDt ? dayjs(i.startDt) : null,
          finish: i.finishDt ? dayjs(i.finishDt) : null
        }));
        setRecords(newRecords);
      })
      .catch(e => message.error(e.userMessage));
    getUsers();
  };

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (!isMobile || users.length === 0) return;
    setSelectedUserId(prev => {
      if (prev !== null && users.some(u => (u.id ?? null) === (prev ?? null))) return prev;
      return users[1]?.id ?? users[0]?.id ?? null;
    });
  }, [users, isMobile]);

  useLayoutEffect(() => {
    if (!isMobile) return;
    const el = recordsContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setMobileGridWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setMobileGridWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [isMobile]);

  useEffect(getRecords, [date, taskId]);

  const filterSummary = useMemo(() => {
    const taskLabel = tasks.find(t => t.value === taskId)?.label ?? String(taskId);
    const parts = [taskLabel];
    if (isMobile) {
      const execLabel = users.find(u => (u.id ?? null) === (selectedUserId ?? null))?.name ?? "—";
      parts.push(execLabel);
    }
    parts.push(date.format("DD.MM.YYYY"));
    if (showDeleted) parts.push("отмененные");
    return parts.join(" · ");
  }, [taskId, date, showDeleted, isMobile, users, selectedUserId]);

  const openFilterModal = () => {
    setDraftTaskId(taskId);
    setDraftDate(date);
    setDraftShowDeleted(showDeleted);
    setDraftSelectedUserId(selectedUserId);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (!draftDate) {
      message.warning("Укажите дату");
      return;
    }
    setTaskId(draftTaskId);
    setDate(draftDate);
    setShowDeleted(draftShowDeleted);
    setSelectedUserId(draftSelectedUserId);
    setFilterOpen(false);
  };

  const cancelFilter = () => setFilterOpen(false);

  const usersForGrid = useMemo(() => {
    if (!isMobile) return users;
    const u = users.find(x => (x.id ?? null) === (selectedUserId ?? null));
    if (u) return [u];
    return users.length ? [users[0]] : [];
  }, [isMobile, users, selectedUserId]);

  const filteredByUser = useMemo(() => {
    let list = records.filter(i => showDeleted || !i.deleted);
    if (isMobile) {
      list = list.filter(i => (i.userId ?? null) === (selectedUserId ?? null));
    }
    return list;
  }, [records, showDeleted, isMobile, selectedUserId]);

  // Ширина столбца времени
  const timeColWidth = 50;
  // Высота шапки
  const headerRowHeight = 50;
  const headerRowHeightForLayout = isMobile ? 0 : headerRowHeight;
  // Высота строки часа
  const hourRowHeight = 50;

  // Функция для сохранения ширины колонки в localStorage
  const saveUserColWidth = (width) => {
    localStorage.setItem('records-userColWidth', width.toString());
  };

  // Обработчики ресайза колонки
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    document.body.classList.add('resizing');
    const startX = e.clientX;
    const startWidth = userColWidth;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(100, Math.min(400, startWidth + deltaX));
      setUserColWidth(newWidth);
      saveUserColWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove('resizing');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const isToday = dayjs(date).isSame(dayjs(), "day");

  const userWidthForLayout = isMobile
    ? Math.max(100, (mobileGridWidth || (typeof window !== "undefined" ? window.innerWidth - 48 : 400)) - timeColWidth)
    : userColWidth;

  const divLines = useMemo(() => {
    const grafLocal = [{ startMin: hourStart * 60, finishMin: hourFinish * 60 }];
    return Array.from({ length: hourFinish - hourStart }, (_, hour) => (
      <RecordsLine
        key={hourStart + hour}
        hour={hourStart + hour}
        users={usersForGrid}
        height={hourRowHeight}
        timeWidth={timeColWidth}
        userWidth={userWidthForLayout}
        isToday={isToday}
        grafs={grafLocal}
        onSelectTime={value => {
          setSelectedTime({ datePlan: date, docDate: dayjs(), ...value, taskId });
        }}
      />
    ));
  }, [
    hourFinish,
    hourStart,
    usersForGrid,
    hourRowHeight,
    timeColWidth,
    userWidthForLayout,
    isToday,
    date,
    taskId,
  ]);

  const onCloseEdit = (refresh) => {
    setSelectedItem(null);
    setSelectedTime(null);
    refresh && getRecords();
  };

  // Обработчики drag and drop для контейнера записей
  const handleContainerDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleContainerDragLeave = (e) => {
    // Проверяем, что мы действительно покинули контейнер, а не перешли к дочернему элементу
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const dragData = JSON.parse(e.dataTransfer.getData("text/plain"));

      const rect = e.currentTarget.getBoundingClientRect();
      const dropX = e.clientX - rect.left;
      const dropY = e.clientY - rect.top;

      const userColForDrop = isMobile ? userWidthForLayout : userColWidth;
      const newColumnIndex = isMobile
        ? 0
        : Math.floor((dropX - timeColWidth) / userColForDrop);
      const newUser = isMobile
        ? users.find(u => (u.id ?? null) === (selectedUserId ?? null)) ?? users[0]
        : users[newColumnIndex];

      const relativeY = dropY - headerRowHeightForLayout;
      const minutesFromStart = (relativeY / hourRowHeight) * 60;
      const roundedMinutes = Math.round(minutesFromStart / 15) * 15;
      const newStart = dayjs().startOf('day').add((hourStart ?? 0) * 60 + roundedMinutes, 'minute');
      
      // Вычисляем новое время окончания (сохраняем длительность)
      const duration = (dragData.finish && dragData.start) ? dayjs(dragData.finish, 'HH:mm').diff(dayjs(dragData.start, 'HH:mm'), 'minute') : 60;
      const newFinish = newStart.add(duration, 'minute');

      // Обновляем запись
      updateRecordOnServer(dragData.id, {
        newUserId: newUser?.id,
        newStart: newStart,
        newFinish: newFinish
      });
    } catch (error) {
      console.error('Error handling container drop:', error);
    }
  };

  // Функция для обновления записи на сервере
  const updateRecordOnServer = async (recordId, newData) => {
    try {
      // Находим запись в локальном состоянии
      const recordIndex = records.findIndex(record => record.id === recordId);
      if (recordIndex === -1) {
        message.error('Запись не найдена');
        return;
      }
      
      // Создаем обновленную запись
      const updatedRecord = {
        ...records[recordIndex],
        userId: newData.newUserId,
        startDt: newData.newStart.format('YYYY-MM-DDTHH:mm:ss'),
        finishDt: newData.newFinish.format('YYYY-MM-DDTHH:mm:ss')
      };

      // Обновляем локальное состояние
      setRecords(prevRecords => {
        const newRecords = [...prevRecords];
        newRecords[recordIndex] = updatedRecord;
        return newRecords;
      });

      // Подготавливаем полную обновленную запись для отправки на сервер
      const updateData = {
        ...updatedRecord,
        docDate: strDate2Server(updatedRecord.docDate),
        // startDt: newData.newStart.format('HH:mm:ss'),
        // startDt: newData.newFinish.format('HH:mm:ss')
      };
      
      // Отправляем запрос на сервер
      await serverFetch(`plan/doc`, { 
        method: 'PUT', 
        bodyData: updateData 
      });
      getRecords();
      message.success('Запись успешно перемещена');
    } catch (error) {
      // В случае ошибки откатываем изменения
      getRecords();
      message.error(error.userMessage || 'Ошибка при перемещении записи');
    }
  };

  const recordsBlock = (
    <div
      ref={isMobile ? recordsContainerRef : undefined}
      className={`records ${isDragOver ? "drag-over" : ""}${isMobile ? " task-plans-records--mobile" : ""}`}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
    >
      {!isMobile && (
        <RecordsHeader
          users={users}
          height={headerRowHeight}
          timeWidth={timeColWidth}
          userWidth={userColWidth}
          records={filteredByUser}
          onResize={handleMouseDown}
        />
      )}
      <div className="records-lines">{divLines}</div>
      {(() => {
        const itemsByUser = {};
        filteredByUser.forEach(item => {
          const userId = item.userId ?? null;
          const userKey = userId === null ? 'null' : userId.toString();
          if (!itemsByUser[userKey]) {
            itemsByUser[userKey] = { userId, withStart: [], withoutStart: [] };
          }
          if (item.start) {
            itemsByUser[userKey].withStart.push(item);
          } else {
            itemsByUser[userKey].withoutStart.push(item);
          }
        });

        // Сортируем записи с null start по id для стабильного порядка
        Object.keys(itemsByUser).forEach(userKey => {
          itemsByUser[userKey].withoutStart.sort((a, b) => (a.id || 0) - (b.id || 0));
        });

        // Создаем массив всех записей с вычисленными позициями
        const itemsWithPositions = [];
        
        Object.keys(itemsByUser).forEach(userKey => {
          const userItems = itemsByUser[userKey];
          const userId = userItems.userId;
          const idx = Math.max(
            0,
            usersForGrid.indexOf(usersForGrid.find(i => (i.id ?? null) === (userId ?? null)))
          );


          let nullStartOffset = 0;
          userItems.withoutStart.forEach(item => {
            const left = timeColWidth + (idx + 0.05) * userWidthForLayout;
            const top = headerRowHeightForLayout + nullStartOffset * hourRowHeight;
            const height = hourRowHeight;
            itemsWithPositions.push({ item, left, top, width: userWidthForLayout * 0.9, height });
            nullStartOffset += 1;
          });

          userItems.withStart.forEach(item => {
            const left = timeColWidth + (idx + 0.05) * userWidthForLayout;
            const top =
              headerRowHeightForLayout +
              ((item.start.diff(item.start.startOf("day"), "minute") - (hourStart ?? 0) * 60) / 60) * hourRowHeight;
            const height =
              item.finish && item.start
                ? (item.finish.diff(item.start, "minute") / 60) * hourRowHeight
                : hourRowHeight;
            itemsWithPositions.push({ item, left, top, width: userWidthForLayout * 0.9, height });
          });
        });

        return itemsWithPositions.map(({ item, left, top, width, height }) => (
          <RecordsItem 
            item={item} 
            left={left} 
            top={top} 
            width={width} 
            height={height} 
            onClick={setSelectedItem} 
            key={item.id}
            timeColWidth={timeColWidth}
            userColWidth={userWidthForLayout}
            headerRowHeight={headerRowHeightForLayout}
            hourRowHeight={hourRowHeight}
            todayUsers={isMobile ? usersForGrid : users}
            updateRecordOnServer={updateRecordOnServer}
            hourStart={hourStart}
          />
        ));
      })()}
    </div>
  );

  return (
    <div className={isMobile ? "page orders-page--mobile" : "page"}>
      {isMobile ? (
        <>
          <div className="orders-mobile-toolbar">
            <div className="company-backups-mobile-filter-row task-plans-mobile-filter-row">
              <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                {filterSummary}
              </span>
              <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                aria-label="Фильтр"
                className="company-backups-mobile-filter-icon-btn"
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={getRecords}
                aria-label="Обновить"
                className="company-backups-mobile-refresh-btn"
              />
            </div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Задача</span>
                    <Select
                      value={draftTaskId}
                      onChange={setDraftTaskId}
                      options={tasks}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Исполнитель</span>
                    <Select
                      value={draftSelectedUserId}
                      onChange={setDraftSelectedUserId}
                      options={users.map(u => ({ value: u.id, label: u.name }))}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Дата</span>
                    <DatePicker
                      value={draftDate}
                      onChange={setDraftDate}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field company-backups-filter-switch-row">
                    <span>Показать отмененные</span>
                    <Checkbox checked={draftShowDeleted} onChange={e => setDraftShowDeleted(e.target.checked)} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="orders-mobile-scroll">{recordsBlock}</div>
        </>
      ) : (
        <>
          <div className="page-cloud-row">
            <div>
              <div>Задача</div>
              <Select value={taskId} onChange={e => setTaskId(e)} options={tasks} style={{ width: 200 }} />
            </div>
            <div>
              <div>Дата</div>
              <DatePicker value={date} onChange={e => setDate(e)} format="DD.MM.YYYY" allowClear={false} style={{ maxWidth: 120 }} />
            </div>
            <Tooltip title="Показать отмененные">
              <Checkbox checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
                Отмененные
              </Checkbox>
            </Tooltip>
            {refreshButton(getRecords)}
          </div>
          {recordsBlock}
        </>
      )}
      {selectedTime && <Record defaultItem={selectedTime} onClose={onCloseEdit} users={users} />}
      {selectedItem && <Record id={selectedItem} onClose={onCloseEdit} users={users} />}
    </div>
  );
}

export default TaskPlans;
